import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * Renders the dropdown menu in a portal on `document.body`, positioned `fixed`
 * to the trigger's bounding rect. Absolutely-positioned menus get clipped by any
 * ancestor with `overflow: hidden/auto` (e.g. cards), so the menu is lifted out
 * of the DOM flow to float freely above everything. Repositions on scroll/resize
 * and closes on outside pointer-downs (checking both the trigger and the menu,
 * since the menu is no longer a descendant of the trigger).
 */
function DropdownPortal({
  anchorRef,
  onRequestClose,
  initialFocusRef,
  menuClassName = '',
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  onRequestClose: () => void;
  /** Focused once the menu is actually mounted and positioned. The portal renders
   *  `null` on its first pass (until `rect` is measured), so the parent can't focus
   *  the search input on open — the element isn't in the DOM yet. Focusing here,
   *  after positioning, is what makes the input immediately typeable. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  menuClassName?: string;
  children: React.ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const update = () => setRect(anchor.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchorRef]);

  // Focus the search input once the menu has been positioned (rect set) and is
  // therefore in the DOM. Runs after the first real render, not on the null pass.
  useLayoutEffect(() => {
    if (rect) initialFocusRef?.current?.focus();
    // Only on first mount/positioning — reposition updates must not steal focus
    // back to the input while the user scrolls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect !== null]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onRequestClose();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [anchorRef, onRequestClose]);

  if (!rect) return null;
  // Flip above the trigger when there isn't room below it in the viewport.
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUp = spaceBelow < 288 && rect.top > spaceBelow;
  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      }}
      className={`z-50 overflow-hidden rounded-md border border-slate-200 bg-white text-slate-900 shadow-lg ${menuClassName}`}
    >
      {children}
    </div>,
    document.body,
  );
}

export interface SearchableDropdownOption<T extends string = string> {
  value: T;
  label: string;
  searchText?: string;
  description?: string;
  disabled?: boolean;
}

export interface SearchableDropdownProps<T extends string = string> {
  options: ReadonlyArray<SearchableDropdownOption<T>>;
  value: T | null;
  onChange: (value: T | null) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  /** @deprecated Menus now always show the complete option list in an eight-row scroll area. */
  initialVisibleCount?: number;
  noResultsText?: string;
  allowClear?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  triggerClassName?: string;
  menuClassName?: string;
  ariaLabel?: string;
  /** Keep the caller's option order instead of collating by label. Use when
   *  the list already has a meaningful order the labels don't sort into — e.g.
   *  reverse-chronological weeks whose spelled-out month labels would otherwise
   *  sort alphabetically. */
  preserveOrder?: boolean;
}

export interface SearchableMultiSelectDropdownProps<T extends string = string> {
  options: ReadonlyArray<SearchableDropdownOption<T>>;
  values: ReadonlyArray<T>;
  onChange: (values: T[]) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  /** @deprecated Menus now always show the complete option list in an eight-row scroll area. */
  initialVisibleCount?: number;
  noResultsText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  triggerClassName?: string;
  menuClassName?: string;
  ariaLabel?: string;
  /** Optional summary shown in the closed trigger instead of selected labels. */
  selectionLabel?: string;
  clearAllLabel?: string;
  preserveOrder?: boolean;
}

const triggerClass =
  'flex min-h-10 w-full items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm transition hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';

const optionLabelCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

function orderedOptions<T extends string>(options: ReadonlyArray<SearchableDropdownOption<T>>) {
  const everyLabelIsNumeric =
    options.length > 0 &&
    options.every((option) => option.label.trim() !== '' && Number.isFinite(Number(option.label.trim())));

  return [...options].sort((left, right) => {
    if (everyLabelIsNumeric) {
      const numericDifference = Number(right.label.trim()) - Number(left.label.trim());
      if (numericDifference !== 0) return numericDifference;
    }
    return optionLabelCollator.compare(left.label, right.label);
  });
}

/**
 * A compact, reusable selector for large option lists. The menu contains every
 * option in an eight-row scroll area and narrows that ordered list when searched.
 */
export function SearchableDropdown<T extends string = string>({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  noResultsText = 'No matching options',
  allowClear = false,
  disabled = false,
  className = '',
  id,
  triggerClassName = '',
  menuClassName = '',
  ariaLabel,
  preserveOrder = false,
}: SearchableDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);
  const sortedOptions = useMemo(
    () => (preserveOrder ? [...options] : orderedOptions(options)),
    [options, preserveOrder],
  );
  const normalizedQuery = query.trim().toLowerCase();
  const matchingOptions = useMemo(() => {
    if (!normalizedQuery) return sortedOptions;
    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
    return sortedOptions.filter((option) => {
      const haystack = `${option.label} ${option.searchText ?? ''} ${option.description ?? ''}`.toLowerCase();
      return queryTokens.every((token) => haystack.includes(token));
    });
  }, [normalizedQuery, sortedOptions]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
  };

  const selectOption = (nextValue: T | null) => {
    onChange(nextValue);
    closeMenu();
  };

  const selectedLabel = selectedOption?.label ?? (value || placeholder);

  return (
    <div className={`w-full ${className}`}>
      {label ? (
        id ? (
          <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </label>
        ) : (
          <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
        )
      ) : null}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`${triggerClass} ${triggerClassName}`}
        onClick={() => (open ? closeMenu() : openMenu())}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        disabled={disabled}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedOption ? 'text-slate-900' : 'text-slate-500'}`}>
          {selectedLabel}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <DropdownPortal
          anchorRef={triggerRef}
          onRequestClose={closeMenu}
          initialFocusRef={searchRef}
          menuClassName={menuClassName}
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') closeMenu();
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm text-slate-900 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div role="listbox" className="max-h-72 overflow-y-auto p-1" aria-label={label ?? placeholder}>
            {allowClear && value !== null ? (
              <button
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => selectOption(null)}
                className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
                Clear selection
              </button>
            ) : null}
            {matchingOptions.length ? (
              matchingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  disabled={option.disabled}
                  onClick={() => selectOption(option.value)}
                  className={`flex w-full items-start gap-2 rounded px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 ${option.value === value ? 'bg-primary/5 text-primary' : 'text-slate-700'}`}
                >
                  <Check
                    className={`mt-0.5 h-4 w-4 shrink-0 ${option.value === value ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.description ? (
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{option.description}</span>
                    ) : null}
                  </span>
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-500">{noResultsText}</p>
            )}
          </div>
        </DropdownPortal>
      ) : null}
    </div>
  );
}

/**
 * Searchable checkbox selector for option lists where more than one value can
 * be active at a time. It intentionally shares the same closed trigger and
 * search behavior as SearchableDropdown.
 */
export function SearchableMultiSelectDropdown<T extends string = string>({
  options,
  values,
  onChange,
  label,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',
  noResultsText = 'No matching options',
  disabled = false,
  className = '',
  id,
  triggerClassName = '',
  menuClassName = '',
  ariaLabel,
  selectionLabel,
  clearAllLabel = 'Clear all',
  preserveOrder = false,
}: SearchableMultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = useMemo(() => new Set(values), [values]);
  const sortedOptions = useMemo(
    () => (preserveOrder ? [...options] : orderedOptions(options)),
    [options, preserveOrder],
  );
  const normalizedQuery = query.trim().toLowerCase();
  const matchingOptions = useMemo(() => {
    if (!normalizedQuery) return sortedOptions;
    const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
    return sortedOptions.filter((option) => {
      const haystack = `${option.label} ${option.searchText ?? ''} ${option.description ?? ''}`.toLowerCase();
      return queryTokens.every((token) => haystack.includes(token));
    });
  }, [normalizedQuery, sortedOptions]);

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
  };

  const toggleOption = (value: T) => {
    onChange(selected.has(value) ? values.filter((current) => current !== value) : [...values, value]);
  };

  const selectedLabels = options.filter((option) => selected.has(option.value)).map((option) => option.label);
  const selectedLabel = selectionLabel ?? (
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]
        : `${selectedLabels.length} selected`
  );

  return (
    <div className={`w-full ${className}`}>
      {label ? (
        id ? (
          <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">
            {label}
          </label>
        ) : (
          <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
        )
      ) : null}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`${triggerClass} ${triggerClassName}`}
        onClick={() => {
          if (disabled) return;
          if (open) closeMenu();
          else {
            setQuery('');
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        disabled={disabled}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedLabels.length ? 'text-slate-900' : 'text-slate-500'}`}>
          {selectedLabel}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <DropdownPortal
          anchorRef={triggerRef}
          onRequestClose={closeMenu}
          initialFocusRef={searchRef}
          menuClassName={menuClassName}
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') closeMenu();
                }}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm text-slate-900 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div
            role="listbox"
            aria-multiselectable="true"
            className="max-h-72 overflow-y-auto p-1"
            aria-label={label ?? placeholder}
          >
            {values.length > 0 ? (
              <button
                type="button"
                onClick={() => onChange([])}
                className="mb-1 flex w-full items-center rounded px-3 py-2 text-left text-xs font-medium text-primary hover:bg-slate-50"
              >
                {clearAllLabel}
              </button>
            ) : null}
            {matchingOptions.length ? (
              matchingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected.has(option.value)}
                  disabled={option.disabled}
                  onClick={() => toggleOption(option.value)}
                  className={`flex w-full items-start gap-2 rounded px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 ${selected.has(option.value) ? 'bg-primary/5 text-primary' : 'text-slate-700'}`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[11px] ${selected.has(option.value) ? 'border-primary bg-primary text-white' : 'border-slate-300'}`}
                    aria-hidden="true"
                  >
                    {selected.has(option.value) ? '✓' : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.description ? (
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{option.description}</span>
                    ) : null}
                  </span>
                </button>
              ))
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-500">{noResultsText}</p>
            )}
          </div>
        </DropdownPortal>
      ) : null}
    </div>
  );
}
